/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */

import {ILogger} from '../../logger/LoggerInterface';
import LoggerFactory from '../../logger/LoggerFactory';
import {ChunkedStreaming} from './ChunkedStreaming';
import {ChunkedStreamingOptions, ChunkedStreamingStatistics} from './StreamTypes';
import {IWebPlayer} from './IWebPlayer';
import {NetworkStates} from './NetworkStates';

const bandwidthToStartAt = 3000000; // Corresponds to 720p in most setups
const minTimeBeforeNextReload = 15000;

type PlayerOptions = {
  preferNative?: boolean;
  originStartTime: number;
  lag: number;
  bandwidthToStartAt: number;
};

export default class WebPlayer {
  private readonly _logger: ILogger = LoggerFactory.getLogger('WebPlayer');
  private readonly _videoElement: HTMLVideoElement;
  private readonly _kind: string;
  private readonly _streamId: string;
  private readonly _manifestURI: string;
  private readonly _options: ChunkedStreamingOptions;
  private readonly _handleStreamFailure: Function;
  private _player: IWebPlayer | null;
  private _lastReloadTime: number;

  constructor(videoElement: HTMLVideoElement, kind: string, streamId: string, manifestURI: string, options: ChunkedStreamingOptions, handleStreamFailure: Function) {
    this._videoElement = videoElement;
    this._kind = kind;
    this._streamId = streamId;
    this._manifestURI = encodeURI(manifestURI).replace(/[#]/g, '%23');
    this._options = options;
    this._player = null;
    this._lastReloadTime = 0;
    this._handleStreamFailure = handleStreamFailure;
  }

  start(): void {
    if (ChunkedStreaming.phenixWebPlayer) {
      const playerOptions: PlayerOptions = {
        ...this._options,
        bandwidthToStartAt
      };

      this._player = new ChunkedStreaming.phenixWebPlayer.WebPlayer(this._logger, this._videoElement, playerOptions);
      this._player?.start(this._manifestURI);

      this._player?.addEventListener('error', errorObject => {
        if (this.canReload() && errorObject && (errorObject.code === 3 || errorObject.severity === ChunkedStreaming.phenixWebPlayer.errors.severity.RECOVERABLE)) {
          this._logger.warn('Reloading unhealthy stream after error event [%j]', errorObject);
          this.reloadIfAble();
        }

        this._logger.error('Cannot reload with error [%j]', errorObject);

        this._handleStreamFailure();
      });

      return;
    }
  }

  isSupported(): boolean {
    return ChunkedStreaming.phenixWebPlayer && ChunkedStreaming.phenixWebPlayer.isSupported;
  }

  getStats(): ChunkedStreamingStatistics {
    if (!this._player) {
      return {
        width: 0,
        height: 0,
        currentTime: 0.0,
        lag: 0.0,
        networkState: NetworkStates.NetworkNoSource,
        dataBuffered: 0.0
      };
    }

    const statistics = this._player.getStats();
    const currentTime = statistics.currentTime || this._videoElement['currentTime'];
    const trueCurrentTime = (Date.now() - this._options.originStartTime) / 1000;

    if (this._videoElement.buffered?.length) {
      statistics.dataBuffered = this._videoElement.buffered.end(0);
    }

    if (statistics.isNative && statistics.deliveryType === 'Hls') {
      statistics.currentTime = currentTime - statistics.lag;
    }

    if (!statistics.currentTime) {
      statistics.currentTime = currentTime;
    }

    if (!statistics.isNative) {
      statistics.lag = Math.max(0.0, trueCurrentTime - currentTime);
    }

    if (this._videoElement.buffered?.length) {
      statistics.networkState = NetworkStates.NetworkLoading;
    } else if (statistics.currentTime > 0) {
      statistics.networkState = NetworkStates.NetworkIdle;
    } else if (statistics.video) {
      statistics.networkState = NetworkStates.NetworkEmpty;
    } else {
      statistics.networkState = NetworkStates.NetworkNoSource;
    }

    return statistics;
  }

  dispose(): void {
    if (this._player) {
      this._player.dispose();
    }

    this._player = null;
  }

  private canReload(): boolean {
    const hasElapsedMinTimeSinceLastReload = !this._lastReloadTime || Date.now() - this._lastReloadTime > minTimeBeforeNextReload;

    return !!this._videoElement && !!this._player && this._videoElement.buffered.length !== 0 && hasElapsedMinTimeSinceLastReload;
  }

  private reloadIfAble(): void {
    if (!this.canReload()) {
      return;
    }

    this._logger.warn('[%s] Reloading unhealthy stream that was active for at least [%s] seconds', this._streamId, minTimeBeforeNextReload / 1000);

    this._lastReloadTime = Date.now();

    this.reload();
  }

  private reload(): void {
    if (this._player) {
      this._player.dispose();
    }

    this._player = null;

    this.start();
  }
}